import React, { useEffect, useRef } from 'react';
import { MemberLocation, Geofence } from '@/types';

interface MemberMapProps {
  members: Array<{ userId: string; name: string; location: MemberLocation }>;
  geofences: Geofence[];
  center?: { lat: number; lng: number };
  onMapReady?: () => void;
}

const DARK_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1e293b' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#94a3b8' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#334155' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f172a' }] },
];

const LocationListFallback: React.FC<{
  members: Array<{ userId: string; name: string; location: MemberLocation }>;
}> = ({ members }) => {
  const now = Date.now();
  return (
    <div className="w-full h-64 rounded-2xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex flex-col gap-2 p-4 overflow-y-auto">
      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Members sharing</p>
      {members.length === 0 && (
        <p className="text-sm text-slate-400 italic">No members currently sharing location</p>
      )}
      {members.map(({ userId, name, location }) => {
        const ageMs = now - location.timestamp.toMillis();
        const ageMin = Math.floor(ageMs / 60000);
        return (
          <div key={userId} className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-brand/20 flex items-center justify-center text-brand font-bold text-sm">
              {name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{name}</p>
              <p className="text-xs text-slate-500">
                {ageMin < 1 ? 'Active now' : `Last seen ${ageMin}m ago`}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const MemberMap: React.FC<MemberMapProps> = ({ members, geofences, center, onMapReady }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const circlesRef = useRef<google.maps.Circle[]>([]);
  const scriptLoadedRef = useRef(false);

  const prefersDark =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;

  const initMap = () => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const defaultCenter =
      center ??
      (members[0]
        ? { lat: members[0].location.lat, lng: members[0].location.lng }
        : { lat: 37.7749, lng: -122.4194 });

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: defaultCenter,
      zoom: 14,
      disableDefaultUI: true,
      styles: prefersDark ? DARK_MAP_STYLES : [],
    });

    onMapReady?.();
  };

  // Load Google Maps script dynamically
  useEffect(() => {
    if (scriptLoadedRef.current) return;
    if (window.google?.maps) {
      scriptLoadedRef.current = true;
      initMap();
      return;
    }

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return;

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      scriptLoadedRef.current = true;
      initMap();
    };
    document.head.appendChild(script);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update geofence circles when fences change
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    circlesRef.current.forEach((c) => c.setMap(null));
    circlesRef.current = geofences.map(
      (fence) =>
        new window.google.maps.Circle({
          center: { lat: fence.lat, lng: fence.lng },
          radius: fence.radiusMeters,
          map: mapInstanceRef.current!,
          fillColor: '#6366f1',
          fillOpacity: 0.12,
          strokeColor: '#6366f1',
          strokeWeight: 2,
        })
    );
  }, [geofences, mapInstanceRef.current]);

  // Update member markers
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const now = Date.now();
    members.forEach(({ userId, name, location }) => {
      const pos = { lat: location.lat, lng: location.lng };
      const isActive = now - location.timestamp.toMillis() < 5 * 60 * 1000;
      if (markersRef.current.has(userId)) {
        markersRef.current.get(userId)!.setPosition(pos);
      } else {
        const marker = new window.google.maps.Marker({
          position: pos,
          map: mapInstanceRef.current!,
          title: name,
          label: {
            text: name.charAt(0).toUpperCase(),
            color: '#fff',
            fontSize: '12px',
          },
          icon: {
            url: `data:image/svg+xml,${encodeURIComponent(
              `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="14" fill="${isActive ? '#22c55e' : '#6366f1'}" stroke="white" stroke-width="2"/><text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-family="sans-serif">${name.charAt(0).toUpperCase()}</text></svg>`
            )}`,
            scaledSize: new window.google.maps.Size(32, 32),
          },
        });
        markersRef.current.set(userId, marker);
      }
    });

    // Remove markers for members no longer in list
    const memberIds = new Set(members.map((m) => m.userId));
    markersRef.current.forEach((marker, uid) => {
      if (!memberIds.has(uid)) {
        marker.setMap(null);
        markersRef.current.delete(uid);
      }
    });
  }, [members]);

  if (!window.google?.maps && !import.meta.env.VITE_GOOGLE_MAPS_API_KEY) {
    return <LocationListFallback members={members} />;
  }

  return (
    <div className="relative w-full h-64 rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700">
      <div ref={mapRef} className="w-full h-full" />
      <div className="absolute bottom-2 right-2 bg-white/90 dark:bg-slate-900/90 text-[9px] font-bold px-2 py-1 rounded-lg text-slate-500 uppercase tracking-wide">
        Web GPS — reduced accuracy
      </div>
      {!window.google?.maps && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
          <p className="text-sm text-slate-400">Loading map…</p>
        </div>
      )}
    </div>
  );
};
